import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, useEffect, useId, useRef } from 'react';
import { Icon, type IconName } from './Icon';

type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: IconName;
}

/** M3 common button. */
export function Button({ variant = 'filled', icon, className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={`btn btn--${variant}${className ? ` ${className}` : ''}`} {...rest}>
      {icon && <Icon name={icon} size={20} />}
      <span>{children}</span>
    </button>
  );
}

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  icon: IconName;
  label: string;
  variant?: 'standard' | 'filled' | 'tonal';
  selected?: boolean;
}

/** M3 icon button; `selected` turns it into a toggle button. */
export function IconButton({ icon, label, variant = 'standard', selected, className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      className={`icon-btn icon-btn--${variant}${selected ? ' is-selected' : ''}${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      aria-pressed={selected}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  supporting?: string;
}

/** M3 outlined text field with floating label. */
export function TextField({ label, error, supporting, className, id, ...rest }: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helpId = `${inputId}-help`;
  return (
    <div className={`text-field${error ? ' has-error' : ''}${className ? ` ${className}` : ''}`}>
      <input id={inputId} placeholder=" " aria-invalid={!!error} aria-describedby={error || supporting ? helpId : undefined} {...rest} />
      <label htmlFor={inputId}>{label}</label>
      {(error ?? supporting) && (
        <p id={helpId} className="text-field__help">
          {error ?? supporting}
        </p>
      )}
    </div>
  );
}

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  icon?: IconName;
}

/** Modal M3 dialog based on the native <dialog> element. */
export function Dialog({ open, title, onClose, children, actions, icon }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {open && (
        <div className="dialog__surface">
          {icon && <Icon name={icon} className="dialog__icon" />}
          <h2 id={titleId} className="dialog__title">
            {title}
          </h2>
          <div className="dialog__content">{children}</div>
          {actions && <div className="dialog__actions">{actions}</div>}
        </div>
      )}
    </dialog>
  );
}

interface ChipProps {
  label: string;
  icon?: IconName;
  selected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
}

/** M3 filter / assist chip. */
export function Chip({ label, icon, selected, onClick, onRemove }: ChipProps) {
  return (
    <span className={`chip${selected ? ' is-selected' : ''}`}>
      <button type="button" className="chip__main" onClick={onClick} aria-pressed={onClick ? !!selected : undefined}>
        {icon && <Icon name={icon} size={18} />}
        <span>{label}</span>
      </button>
      {onRemove && (
        <button type="button" className="chip__remove" aria-label={`${label} entfernen`} onClick={onRemove}>
          <Icon name="close" size={18} />
        </button>
      )}
    </span>
  );
}

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
}

/** M3 single-select segmented button. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`segmented__item${o.value === value ? ' is-selected' : ''}`}
          onClick={() => onChange(o.value)}
        >
          <Icon
            name={o.value === value ? 'check' : (o.icon ?? 'check')}
            size={18}
            className={o.value === value || o.icon ? '' : 'is-hidden'}
          />
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Indeterminate linear progress indicator. */
export function LinearProgress({ active, label }: { active: boolean; label: string }) {
  return <div className={`linear-progress${active ? ' is-active' : ''}`} role="progressbar" aria-label={label} aria-busy={active} />;
}

/** Centered empty-state illustration. */
export function EmptyState({ icon, title, text }: { icon: IconName; title: string; text?: string }) {
  return (
    <div className="empty-state">
      <div className="empty-state__shape">
        <Icon name={icon} size={48} />
      </div>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}
